import { useEffect, useMemo, useState, type ReactNode } from "react";
import clsx from "clsx";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox, SelectInput, TextInput, TextareaInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  getFieldTypeLabel,
  LAYOUT_FIELD_TYPES,
  MAINTENANCE_GRADE_COLORS,
  OPTION_FIELD_TYPES,
  SEMANTIC_ROLES,
} from "@/lib/fieldTypeCatalog";
import type { MaintenanceType } from "@/lib/wartungen";
import { searchMaterials } from "@/lib/wartungen";
import { slugifyKey } from "@/pages/wartungen/lib/wartungenUi";
import {
  getFieldEditorCapabilities,
  isPureStructureField,
} from "./fieldEditorCapabilities";
import {
  BEHAVIOR_OPERATOR_LABELS,
  BEHAVIOR_SOURCE_SCOPE_LABELS,
  BEHAVIOR_SOURCE_SCOPES,
  coerceRuleValueForOperator,
  getAvailableSourceFields,
  isBooleanSourceField,
  mergeDuplicateEqualityRules,
  normalizeSourceScope,
  normalizeVisibilityRule,
  operatorChoicesForSourceField,
} from "./behaviorRules";
import { getAnswerRulesFromBehavior } from "./answerRules";
import { AnswerRulesEditor } from "./AnswerRulesEditor";
import { RuleValueInput } from "./RuleValueInput";
import {
  type ArticleRef,
  type BuilderField,
  type FieldOption,
  type VisibilityRule,
  normalizeBehavior,
} from "./fieldModel";
import { beginDrag, endDrag, getDrag } from "./dragState";
import { getFieldTypeHint, getFieldTypeIcon } from "./fieldTypeIcons";
import {
  countDefectOptions,
  defaultDefectTriggerForType,
  fieldSupportsDefectTrigger,
  getDefectTriggerGradeEffect,
  isBooleanDefectType,
  isOptionDefectType,
  materializeDefectTrigger,
  parseDefectTrigger,
  resolveDefectTrigger,
  stripDefectTriggerIfNotDefect,
  type DefectTrigger,
} from "./defectTrigger";
import { buildDefectTriggerGuidance } from "./publishValidation";

interface FieldEditorProps {
  field: BuilderField;
  allFields: BuilderField[];
  enabledGrades: number[];
  maintenanceTypes: MaintenanceType[];
  onApply: (field: BuilderField) => void;
  onCancel: () => void;
}

type EditorTab = "basics" | "rules" | "display";

const TAB_LABELS: Record<EditorTab, string> = {
  basics: "Grundlagen",
  rules: "Regeln",
  display: "Darstellung & Technik",
};

function getOptions(field: BuilderField): FieldOption[] {
  const raw = field.options_override_json.options;
  return Array.isArray(raw) ? (raw as FieldOption[]) : [];
}

function prepareDraft(field: BuilderField): BuilderField {
  const draft = structuredClone(field);
  // Das, was der Editor anzeigt, muss auch im Datenmodell stehen.
  materializeDefectTrigger(draft);
  return draft;
}

/* ── Kleine Layout-Bausteine ─────────────────────────────────────────────── */

function Section({
  title,
  description,
  aside,
  tone,
  children,
}: {
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
  tone?: "default" | "defect";
  children: ReactNode;
}) {
  return (
    <section className={clsx("mb-editor-section", tone === "defect" && "mb-editor-section--defect")}>
      <header className="mb-editor-section__head">
        <div className="min-w-0">
          <h5 className="mb-editor-section__title">{title}</h5>
          {description ? <p className="mb-editor-section__desc">{description}</p> : null}
        </div>
        {aside ? <div className="mb-editor-section__aside">{aside}</div> : null}
      </header>
      <div className="mb-editor-section__body">{children}</div>
    </section>
  );
}

function StatusChip({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "error" | "neutral";
  children: ReactNode;
}) {
  const Icon =
    tone === "ok" ? CheckCircleIcon : tone === "neutral" ? InformationCircleIcon : ExclamationTriangleIcon;
  return (
    <span className={clsx("mb-status-chip", `mb-status-chip--${tone}`)}>
      <Icon aria-hidden />
      {children}
    </span>
  );
}

/* ── Editor ──────────────────────────────────────────────────────────────── */

export function FieldEditor({
  field,
  allFields,
  enabledGrades,
  maintenanceTypes,
  onApply,
  onCancel,
}: FieldEditorProps) {
  const [draft, setDraft] = useState(() => prepareDraft(field));
  const [tab, setTab] = useState<EditorTab>("basics");

  useEffect(() => {
    setDraft(prepareDraft(field));
    setTab("basics");
  }, [field.uid]);

  const isLayout = LAYOUT_FIELD_TYPES.has(draft.type);
  const caps = getFieldEditorCapabilities(draft.type);
  const hasOptions = OPTION_FIELD_TYPES.has(draft.type);
  const isArticleType = draft.type === "article_ref" || draft.type === "article_ref_list";
  const isInspection = draft.scope === "inspection";
  const showSemanticRole = isInspection && caps.semanticRole;
  const isDefectRole = showSemanticRole && draft.semantic_role === "defect";
  const defectTriggerSupported = fieldSupportsDefectTrigger(draft.type);
  const isPureStructure = isPureStructureField(draft.type);
  const TypeIcon = getFieldTypeIcon(draft.type);

  const ui = draft.ui_override_json;
  const options = getOptions(draft);

  const resolvedTrigger = isDefectRole ? resolveDefectTrigger(draft) : null;
  const defectStatus: { tone: "ok" | "warn" | "error"; text: string } | null = useMemo(() => {
    if (!isDefectRole) return null;
    if (!defectTriggerSupported) return { tone: "warn", text: "Rolle passt nicht zum Feldtyp" };
    if (!resolvedTrigger) return { tone: "error", text: "Mangel-Regel fehlt" };
    return { tone: "ok", text: "Mangel-Regel vollständig" };
  }, [isDefectRole, defectTriggerSupported, resolvedTrigger]);

  const visibilityRuleCount = Array.isArray(draft.behavior_override_json?.rules)
    ? (draft.behavior_override_json.rules as unknown[]).length
    : 0;
  const answerRuleCount = getAnswerRulesFromBehavior(draft.behavior_override_json).length;

  const showDisplayTab =
    !isPureStructure &&
    (draft.type === "checkbox" ||
      draft.type === "photo" ||
      draft.type === "static_text" ||
      caps.placeholder ||
      caps.helpText ||
      caps.fieldKey);
  const showRulesTab = !isPureStructure && (caps.visibilityRules || caps.answerRules);

  const tabs: EditorTab[] = ["basics"];
  if (showRulesTab) tabs.push("rules");
  if (showDisplayTab) tabs.push("display");
  const activeTab: EditorTab = tabs.includes(tab) ? tab : "basics";

  function onPatch(mutator: (f: BuilderField) => void) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      mutator(next);
      return next;
    });
  }

  function setUi(key: string, value: unknown) {
    onPatch((f) => {
      f.ui_override_json = { ...f.ui_override_json, [key]: value };
    });
  }
  function setOptionsOverride(key: string, value: unknown) {
    onPatch((f) => {
      f.options_override_json = { ...f.options_override_json, [key]: value };
    });
  }
  function setOptions(next: FieldOption[]) {
    onPatch((f) => {
      f.options_override_json = { ...f.options_override_json, options: next };
    });
  }
  function setRole(role: string) {
    onPatch((f) => {
      f.semantic_role = role;
      if (role === "defect") {
        materializeDefectTrigger(f);
      } else {
        stripDefectTriggerIfNotDefect(f);
      }
    });
  }

  function handleApply() {
    const next = structuredClone(draft);
    if (next.type === "material_link") {
      next.semantic_role = "material";
    } else if (!caps.semanticRole || !isInspection) {
      next.semantic_role = "info";
    }
    if (!caps.required || isLayout) next.required = false;
    materializeDefectTrigger(next);
    stripDefectTriggerIfNotDefect(next);
    onApply(next);
  }

  const footer = (
    <footer className="mb-editor__footer">
      <div className="mb-editor__footer-hint">
        {defectStatus ? <StatusChip tone={defectStatus.tone}>{defectStatus.text}</StatusChip> : null}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="button" size="sm" onClick={handleApply}>
          Übernehmen
        </Button>
      </div>
    </footer>
  );

  return (
    <div className="mb-editor">
      {/* ── Kopfzeile: Typ-Hinweis + Bereichs-Tabs ── */}
      <div className="mb-editor__head">
        <p className="mb-editor__eyebrow">
          <TypeIcon aria-hidden />
          <span>
            {getFieldTypeLabel(draft.type)}
            {getFieldTypeHint(draft.type) ? ` · ${getFieldTypeHint(draft.type)}` : ""}
            {" · "}
            {isInspection ? "Wartungsformular" : "Stammdaten"}
          </span>
        </p>
      </div>

      {tabs.length > 1 ? (
        <SegmentedControl<EditorTab>
          className="mb-editor__tabs"
          name="Editor-Bereiche"
          value={activeTab}
          onChange={setTab}
          options={tabs.map((t) => ({
            value: t,
            label: (
              <>
                {TAB_LABELS[t]}
                {t === "rules" && visibilityRuleCount + answerRuleCount > 0 ? (
                  <span className="mb-editor__tab-count">{visibilityRuleCount + answerRuleCount}</span>
                ) : null}
                {t === "basics" && defectStatus && defectStatus.tone !== "ok" ? (
                  <span className="mb-editor__tab-dot" aria-label={defectStatus.text} />
                ) : null}
              </>
            ),
          }))}
        />
      ) : null}

      <div className="mb-editor__panel">
        {/* ═══ Grundlagen ═══ */}
        {activeTab === "basics" ? (
          <>
            <Section
              title="Allgemein"
              description={
                isPureStructure
                  ? draft.type === "page_break"
                    ? "Seitenumbruch – teilt das Formular in Abschnitte. Keine weiteren fachlichen Einstellungen nötig."
                    : "Trennlinie – optische Gliederung ohne Eingabewert."
                  : undefined
              }
            >
              {!isPureStructure ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {caps.label ? (
                    <TextInput
                      label="Feldname (Label)"
                      value={draft.label}
                      placeholder="z. B. Weisen die Elemente Beschädigungen auf?"
                      onChange={(e) => onPatch((f) => { f.label = e.target.value; })}
                    />
                  ) : null}
                  {caps.category ? (
                    <TextInput
                      label="Feldgruppe"
                      value={draft.category}
                      placeholder="z. B. Sichtprüfung"
                      hint="Felder derselben Gruppe werden im Formular zusammen angezeigt."
                      onChange={(e) => onPatch((f) => { f.category = e.target.value; })}
                    />
                  ) : null}
                  {showSemanticRole ? (
                    <div className="space-y-1 sm:col-span-2">
                      <span className="eh-label">Rolle im Wartungsformular</span>
                      <SegmentedControl
                        name="Rolle"
                        value={draft.semantic_role}
                        onChange={setRole}
                        options={SEMANTIC_ROLES.map((r) => ({ value: r.value, label: r.label }))}
                      />
                      <p className="text-xs text-label-secondary">
                        {draft.semantic_role === "defect"
                          ? "Antworten dieses Feldes können einen Mangel auslösen und die Bewertung beeinflussen."
                          : draft.semantic_role === "material"
                            ? "Erfasst Material, das bei der Wartung verwendet wurde."
                            : draft.semantic_role === "measurement"
                              ? "Erfasst einen Messwert ohne direkte Mangel-Wirkung."
                              : draft.semantic_role === "grade"
                                ? "Direkte Bewertung (Note) durch das Wartungsteam."
                                : "Reine Information, keine Auswirkung auf Mängel oder Noten."}
                      </p>
                    </div>
                  ) : null}
                  {isDefectRole && !defectTriggerSupported ? (
                    <div className="sm:col-span-2">
                      <Alert variant="warning">{buildDefectTriggerGuidance(draft)}</Alert>
                    </div>
                  ) : null}
                  {caps.required ? (
                    <div className="sm:col-span-2">
                      <Checkbox
                        id={`req-${draft.uid}`}
                        label="Pflichtfeld"
                        description="Muss vom Wartungsteam ausgefüllt werden, bevor die Wartung abgeschlossen werden kann."
                        checked={draft.required}
                        onChange={(e) => onPatch((f) => { f.required = e.target.checked; })}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Section>

            {/* ── Mangelbewertung (Ja/Nein) ── */}
            {isDefectRole && isBooleanDefectType(draft.type) ? (
              <DefectCheckboxSection
                draft={draft}
                enabledGrades={enabledGrades}
                onSetOptionsOverride={setOptionsOverride}
              />
            ) : null}

            {/* ── Optionen ── */}
            {hasOptions ? (
              <OptionsEditor
                field={draft}
                options={options}
                isArticleType={isArticleType}
                isDefectRole={isDefectRole}
                enabledGrades={enabledGrades}
                onSetOptions={setOptions}
                onSetOptionsOverride={setOptionsOverride}
                onSetRole={setRole}
              />
            ) : null}

            {/* ── Verknüpftes Wartungsobjekt ── */}
            {draft.type === "linked_asset" ? (
              <Section title="Verknüpftes Wartungsobjekt">
                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectInput
                    label="Ziel-Kategorie"
                    value={String(draft.options_override_json.target_maintenance_type_key || "")}
                    onChange={(e) => setOptionsOverride("target_maintenance_type_key", e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    {maintenanceTypes.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.name_plural || t.name || t.key}
                      </option>
                    ))}
                  </SelectInput>
                  <SelectInput
                    label="Kardinalität"
                    value={String(draft.options_override_json.cardinality || "single")}
                    onChange={(e) => setOptionsOverride("cardinality", e.target.value)}
                  >
                    <option value="single">Einzeln</option>
                    <option value="multi">Mehrere</option>
                  </SelectInput>
                  <Checkbox
                    id={`bidir-${draft.uid}`}
                    label="Bidirektional verknüpfen"
                    checked={Boolean(draft.options_override_json.is_bidirectional)}
                    onChange={(e) => setOptionsOverride("is_bidirectional", e.target.checked)}
                  />
                </div>
              </Section>
            ) : null}

            {/* ── Materialverknüpfung ── */}
            {draft.type === "material_link" ? (
              <MaterialLinkEditor draft={draft} allFields={allFields} onPatch={onPatch} />
            ) : null}
          </>
        ) : null}

        {/* ═══ Regeln ═══ */}
        {activeTab === "rules" ? (
          <>
            {caps.visibilityRules ? (
              <RulesEditor field={draft} allFields={allFields} onPatch={onPatch} />
            ) : null}
            {caps.answerRules ? (
              <AnswerRulesEditor field={draft} allFields={allFields} onPatch={onPatch} />
            ) : null}
          </>
        ) : null}

        {/* ═══ Darstellung & Technik ═══ */}
        {activeTab === "display" ? (
          <>
            {draft.type === "checkbox" ||
            draft.type === "photo" ||
            draft.type === "static_text" ||
            caps.placeholder ||
            caps.helpText ? (
              <Section title="Darstellung" description="Wie das Feld im Wartungsformular erscheint.">
                <div className="grid gap-3 sm:grid-cols-2">
                  {draft.type === "checkbox" ? (
                    <>
                      <TextInput
                        label="Beschriftung „Ja“"
                        value={String(ui.labelTrue || "")}
                        placeholder="Ja"
                        onChange={(e) => setUi("labelTrue", e.target.value)}
                      />
                      <TextInput
                        label="Beschriftung „Nein“"
                        value={String(ui.labelFalse || "")}
                        placeholder="Nein"
                        onChange={(e) => setUi("labelFalse", e.target.value)}
                      />
                    </>
                  ) : null}
                  {draft.type === "photo" ? (
                    <Checkbox
                      id={`multi-${draft.uid}`}
                      label="Mehrere Fotos erlauben"
                      checked={Boolean(ui.allow_multiple_photos)}
                      onChange={(e) => setUi("allow_multiple_photos", e.target.checked)}
                    />
                  ) : null}
                  {draft.type === "static_text" ? (
                    <>
                      <SelectInput
                        label="Variante"
                        value={String(ui.static_variant || "info")}
                        onChange={(e) => setUi("static_variant", e.target.value)}
                      >
                        <option value="info">Information</option>
                        <option value="heading">Überschrift</option>
                        <option value="warnung">Warnung</option>
                      </SelectInput>
                      <TextareaInput
                        label="Statischer Inhalt"
                        className="sm:col-span-2"
                        value={String(ui.static_content || "")}
                        onChange={(e) => setUi("static_content", e.target.value)}
                      />
                    </>
                  ) : null}
                  {caps.placeholder && draft.type !== "checkbox" ? (
                    <TextInput
                      label="Platzhalter"
                      value={String(ui.placeholder || "")}
                      onChange={(e) => setUi("placeholder", e.target.value)}
                    />
                  ) : null}
                  {caps.helpText ? (
                    <TextInput
                      label="Hilfetext"
                      className="sm:col-span-2"
                      hint="Wird unter dem Feld als Erläuterung angezeigt."
                      value={String(ui.help_text || "")}
                      onChange={(e) => setUi("help_text", e.target.value)}
                    />
                  ) : null}
                </div>
              </Section>
            ) : null}

            {caps.fieldKey ? (
              <Section
                title="Technik & Layout"
                description="Nur ändern, wenn nötig – der Feldschlüssel identifiziert das Feld in Auswertungen und Regeln."
              >
                <div className="grid gap-3 sm:grid-cols-4">
                  <TextInput
                    label="Feldschlüssel"
                    value={draft.field_key}
                    className="sm:col-span-2"
                    inputClassName="font-mono text-xs"
                    onChange={(e) => onPatch((f) => { f.field_key = slugifyKey(e.target.value); })}
                  />
                  <TextInput
                    label="Zeile"
                    type="number"
                    min={0}
                    value={draft.layout_row}
                    onChange={(e) => onPatch((f) => { f.layout_row = Math.max(0, Number(e.target.value) || 0); })}
                  />
                  <TextInput
                    label="Spalte (0–2)"
                    type="number"
                    min={0}
                    max={2}
                    value={draft.layout_col}
                    onChange={(e) => onPatch((f) => { f.layout_col = Math.max(0, Math.min(2, Number(e.target.value) || 0)); })}
                  />
                  <TextInput
                    label="Breite (Span 1–3)"
                    type="number"
                    min={1}
                    max={3}
                    value={draft.layout_span}
                    onChange={(e) => onPatch((f) => {
                      const span = Math.max(1, Math.min(3, Number(e.target.value) || 1));
                      f.layout_span = span;
                      f.layout_weight = span;
                    })}
                  />
                  <p className="text-xs text-label-secondary sm:col-span-4">
                    Feldtyp: {getFieldTypeLabel(draft.type)} (nach Anlage nicht änderbar)
                  </p>
                </div>
              </Section>
            ) : null}
          </>
        ) : null}
      </div>

      {footer}
    </div>
  );
}

/* ── Mangelbewertung für Ja/Nein-Felder ──────────────────────────────────── */

function DefectCheckboxSection({
  draft,
  enabledGrades,
  onSetOptionsOverride,
}: {
  draft: BuilderField;
  enabledGrades: number[];
  onSetOptionsOverride: (key: string, value: unknown) => void;
}) {
  const trigger: DefectTrigger =
    parseDefectTrigger(draft.options_override_json.defect_trigger) ??
    defaultDefectTriggerForType(draft.type) ??
    { mode: "equals", values: [true] };
  const triggerOnTrue = String(trigger.values[0]).toLowerCase() !== "false";
  const grade = getDefectTriggerGradeEffect(trigger);
  const labelTrue = String(draft.ui_override_json.labelTrue || "").trim() || "Ja";
  const labelFalse = String(draft.ui_override_json.labelFalse || "").trim() || "Nein";

  function update(partial: { values?: DefectTrigger["values"]; grade_effect?: number | null }) {
    // Immer einen vollständigen Trigger schreiben (mode + values), nie nur grade_effect.
    const next: DefectTrigger = { mode: "equals", values: partial.values ?? trigger.values };
    const nextGrade = partial.grade_effect === undefined ? grade : partial.grade_effect;
    if (nextGrade != null) next.grade_effect = nextGrade;
    onSetOptionsOverride("defect_trigger", next);
  }

  return (
    <Section
      title="Mangelbewertung"
      tone="defect"
      description="Legt fest, welche Antwort als Mangel gilt und wie sie sich auf die Note auswirkt."
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="eh-label">Mangel bei Antwort</span>
          <SegmentedControl<"true" | "false">
            name="Mangel bei Antwort"
            value={triggerOnTrue ? "true" : "false"}
            onChange={(v) => update({ values: [v === "true"] })}
            options={[
              { value: "true", label: `${labelTrue} (angehakt)` },
              { value: "false", label: `${labelFalse} (nicht angehakt)` },
            ]}
          />
        </div>
        <GradeSelect
          label="Auswirkung auf Note"
          enabledGrades={enabledGrades}
          value={grade}
          onChange={(g) => update({ grade_effect: g })}
        />
      </div>
      <p className="mb-defect-summary">
        <CheckCircleIcon aria-hidden />
        <span>
          Mangel wird ausgelöst, wenn <strong>„{triggerOnTrue ? labelTrue : labelFalse}“</strong> gewählt wird
          {grade != null ? (
            <>
              {" "}– Note{" "}
              <strong style={{ color: MAINTENANCE_GRADE_COLORS[grade] }}>{grade}</strong>
            </>
          ) : (
            " – ohne Einfluss auf die Note"
          )}
          .
        </span>
      </p>
    </Section>
  );
}

/* ── Optionen-Editor mit DnD-Reihenfolge ─────────────────────────────────── */

function OptionsEditor({
  field,
  options,
  isArticleType,
  isDefectRole,
  enabledGrades,
  onSetOptions,
  onSetOptionsOverride,
  onSetRole,
}: {
  field: BuilderField;
  options: FieldOption[];
  isArticleType: boolean;
  isDefectRole: boolean;
  enabledGrades: number[];
  onSetOptions: (next: FieldOption[]) => void;
  onSetOptionsOverride: (key: string, value: unknown) => void;
  onSetRole: (role: string) => void;
}) {
  const allowFreeInput = Boolean(field.options_override_json.allow_free_input);
  const defectCount = countDefectOptions(field.options_override_json);
  const optionDefectSupported = isOptionDefectType(field.type);

  function updateOption(idx: number, patch: Partial<FieldOption>) {
    onSetOptions(options.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }
  function addOption() {
    const n = options.length + 1;
    onSetOptions([
      ...options,
      { label: `Option ${n}`, value: slugifyKey(`option_${n}`), active: true, sort_order: n * 10 },
    ]);
  }
  function removeOption(idx: number) {
    onSetOptions(options.filter((_, i) => i !== idx).map((o, i) => ({ ...o, sort_order: (i + 1) * 10 })));
  }
  function moveOption(from: number, to: number) {
    if (from === to || to < 0 || to >= options.length) return;
    const next = [...options];
    const [m] = next.splice(from, 1);
    if (!m) return;
    next.splice(to, 0, m);
    onSetOptions(next.map((o, i) => ({ ...o, sort_order: (i + 1) * 10 })));
  }
  function markAllAsDefect() {
    onSetOptions(options.map((o) => ({ ...o, is_defect: true })));
  }

  const showDefectColumn = isDefectRole && optionDefectSupported;

  return (
    <Section
      title="Optionen"
      tone={showDefectColumn ? "defect" : "default"}
      description={
        showDefectColumn
          ? "Markiere jede Auswahl, die als Mangel gilt. Optional kann je Option eine Note hinterlegt werden."
          : "Auswahlmöglichkeiten im Formular. Reihenfolge per Ziehen am Griff ändern."
      }
      aside={
        showDefectColumn && options.length ? (
          <StatusChip tone={defectCount.defect > 0 ? "ok" : "error"}>
            {defectCount.defect} von {defectCount.total} als Mangel
          </StatusChip>
        ) : null
      }
    >
      {showDefectColumn && options.length > 0 && defectCount.defect === 0 ? (
        <Alert variant="warning" className="mb-3">
          <div className="space-y-2">
            <p>
              Noch keine Option als „Mangel“ markiert. Ohne Markierung kann dieses Feld keinen Mangel auslösen –
              das Formular lässt sich dann nicht publizieren.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={markAllAsDefect}>
                Alle Optionen als Mangel markieren
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => onSetRole("info")}>
                Rolle auf „Information“ setzen
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}
      {showDefectColumn && options.length === 0 ? (
        <Alert variant="warning" className="mb-3">
          Dieses Mangel-Feld hat noch keine Optionen. Lege Optionen an und markiere mindestens eine als „Mangel“.
        </Alert>
      ) : null}

      <div className="mb-option-list">
        {options.length > 0 ? (
          <div className={clsx("mb-option-list__head", showDefectColumn && "mb-option-list__head--defect")}>
            <span />
            <span>Beschriftung</span>
            <span>Wert (technisch)</span>
            {showDefectColumn ? <span>Mangel</span> : <span />}
            <span />
          </div>
        ) : (
          <p className="text-xs text-label-secondary">Noch keine Optionen.</p>
        )}
        {options.map((opt, idx) => (
          <OptionRow
            key={idx}
            opt={opt}
            idx={idx}
            fieldUid={field.uid}
            isArticleType={isArticleType}
            isDefectRole={showDefectColumn}
            enabledGrades={enabledGrades}
            onUpdate={updateOption}
            onRemove={removeOption}
            onMove={moveOption}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" variant="secondary" onClick={addOption}>
          Option hinzufügen
        </Button>
        {!isArticleType ? (
          <Checkbox
            id={`free-${field.uid}`}
            label="Freie Eingabe erlauben"
            checked={allowFreeInput}
            onChange={(e) => onSetOptionsOverride("allow_free_input", e.target.checked)}
          />
        ) : null}
      </div>
    </Section>
  );
}

function optionValueFromLabel(label: string): string {
  return slugifyKey(label) || "option";
}

function OptionRow({
  opt,
  idx,
  fieldUid,
  isArticleType,
  isDefectRole,
  enabledGrades,
  onUpdate,
  onRemove,
  onMove,
}: {
  opt: FieldOption;
  idx: number;
  fieldUid: string;
  isArticleType: boolean;
  isDefectRole: boolean;
  enabledGrades: number[];
  onUpdate: (idx: number, patch: Partial<FieldOption>) => void;
  onRemove: (idx: number) => void;
  onMove: (from: number, to: number) => void;
}) {
  const [dropClass, setDropClass] = useState("");
  const [valueUserEdited, setValueUserEdited] = useState(false);
  const isDefect = opt.is_defect === true;

  function startOptionDrag(e: React.DragEvent) {
    beginDrag({ kind: "inline-option", scope: "inspection", optionIndex: idx, optionFieldUid: fieldUid });
    e.dataTransfer.setData("application/x-maintenance-drag-type", "inline-option");
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation();
    e.currentTarget.classList.add("is-drag-ghost");
  }

  return (
    <div
      className={clsx("mb-option-row", isDefectRole && "mb-option-row--defect", isDefect && "is-defect", dropClass)}
      onDragOver={(e) => {
        const drag = getDrag();
        if (drag?.kind !== "inline-option" || drag.optionFieldUid !== fieldUid) return;
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const before = e.clientY - rect.top < rect.height / 2;
        setDropClass(before ? "is-inline-drop-before" : "is-inline-drop-after");
      }}
      onDragLeave={() => setDropClass("")}
      onDrop={(e) => {
        const drag = getDrag();
        if (drag?.kind !== "inline-option" || drag.optionFieldUid !== fieldUid) return;
        e.preventDefault();
        e.stopPropagation();
        const from = drag.optionIndex ?? idx;
        const rect = e.currentTarget.getBoundingClientRect();
        const before = e.clientY - rect.top < rect.height / 2;
        const to = before ? idx : idx + 1;
        onMove(from, from < to ? to - 1 : to);
        setDropClass("");
        endDrag();
      }}
    >
      <span
        className="mb-option-row__handle"
        title="Ziehen zum Sortieren"
        draggable
        onDragStart={startOptionDrag}
        onDragEnd={(e) => {
          endDrag();
          setDropClass("");
          e.currentTarget.classList.remove("is-drag-ghost");
        }}
      >
        ⠿
      </span>
      <input
        className="eh-input mb-option-row__input"
        value={opt.label || ""}
        placeholder="Beschriftung"
        aria-label={`Beschriftung Option ${idx + 1}`}
        draggable={false}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          const label = e.target.value;
          const patch: Partial<FieldOption> = { label };
          if (!valueUserEdited) patch.value = optionValueFromLabel(label);
          onUpdate(idx, patch);
        }}
      />
      <input
        className="eh-input mb-option-row__input mb-option-row__input--mono"
        value={opt.value || ""}
        placeholder="Wert"
        aria-label={`Wert Option ${idx + 1}`}
        draggable={false}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          setValueUserEdited(true);
          onUpdate(idx, { value: e.target.value.replace(/[^a-zA-Z0-9._:-]/g, "") });
        }}
      />
      {isDefectRole ? (
        <div className="mb-option-row__defect">
          <button
            type="button"
            className={clsx("mb-defect-toggle", isDefect && "is-on")}
            aria-pressed={isDefect}
            title={isDefect ? "Diese Option löst einen Mangel aus" : "Als Mangel markieren"}
            onClick={() => onUpdate(idx, { is_defect: !isDefect })}
          >
            <ExclamationTriangleIcon aria-hidden />
            Mangel
          </button>
          {isDefect ? (
            <select
              className="eh-input mb-option-row__grade"
              aria-label="Note bei Mangel"
              value={String(opt.grade_effect ?? "")}
              onChange={(e) => onUpdate(idx, { grade_effect: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Note –</option>
              {enabledGrades.map((g) => (
                <option key={g} value={g}>
                  Note {g}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      ) : (
        <span />
      )}
      <button
        type="button"
        className="mb-icon-btn mb-icon-btn--danger"
        title="Option entfernen"
        aria-label="Option entfernen"
        onClick={() => onRemove(idx)}
      >
        <TrashIcon aria-hidden />
      </button>
      {isArticleType ? (
        <div className="col-span-full">
          <ArticlePicker
            articles={Array.isArray(opt.articles) ? opt.articles : []}
            onChange={(articles) => onUpdate(idx, { articles })}
          />
        </div>
      ) : null}
    </div>
  );
}

function ArticlePicker({
  articles,
  onChange,
}: {
  articles: ArticleRef[];
  onChange: (next: ArticleRef[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<{ label: string; hero_nr?: string; artikel_id?: number | string }[]>([]);
  const [searching, setSearching] = useState(false);

  async function runSearch(q: string) {
    setQuery(q);
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    try {
      const res = await searchMaterials(q.trim());
      setSuggestions(
        res.slice(0, 8).map((m) => ({
          label: String(m.label || m.name || m.bezeichnung || m.hero_nr || ""),
          hero_nr: m.hero_nr,
          artikel_id: m.artikel_id ?? m.id,
        })),
      );
    } catch {
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="mt-1 rounded-lg border border-separator bg-card p-2">
      <div className="flex flex-wrap gap-1">
        {articles.map((a, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-full border border-separator bg-fill-tertiary px-2 py-0.5 text-xs"
          >
            {a.label || a.hero_nr || a.artikel_id}
            <input
              type="number"
              className="h-5 w-12 rounded border border-separator px-1 text-xs"
              value={a.qty ?? 1}
              onChange={(e) =>
                onChange(articles.map((x, j) => (j === i ? { ...x, qty: Number(e.target.value) || 1 } : x)))
              }
            />
            <button
              type="button"
              className="text-status-red"
              onClick={() => onChange(articles.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        className="eh-input mt-1"
        placeholder="Artikel suchen (Hero-Nr. / Bezeichnung) …"
        value={query}
        onChange={(e) => void runSearch(e.target.value)}
      />
      {searching ? <p className="mt-1 text-xs text-label-tertiary">Suche …</p> : null}
      {suggestions.length ? (
        <ul className="mt-1 max-h-40 overflow-auto rounded-lg border border-separator">
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                className="block w-full px-2 py-1 text-left text-xs hover:bg-fill-tertiary"
                onClick={() => {
                  onChange([...articles, { label: s.label, hero_nr: s.hero_nr, artikel_id: s.artikel_id, qty: 1 }]);
                  setQuery("");
                  setSuggestions([]);
                }}
              >
                {s.label} {s.hero_nr ? `· ${s.hero_nr}` : ""}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ── Sichtbarkeitsregeln (visibleIf) ─────────────────────────────────────── */

function RulesEditor({
  field,
  allFields,
  onPatch,
}: {
  field: BuilderField;
  allFields: BuilderField[];
  onPatch: (mutator: (f: BuilderField) => void) => void;
}) {
  const behavior = normalizeBehavior(field.behavior_override_json);
  const rawRules = Array.isArray(behavior.rules) ? (behavior.rules as VisibilityRule[]) : [];
  const rules = rawRules.map(normalizeVisibilityRule);
  const availableFields = getAvailableSourceFields(allFields, field);

  function setRules(next: VisibilityRule[]) {
    onPatch((f) => {
      f.behavior_override_json = { ...normalizeBehavior(f.behavior_override_json), rules: next };
    });
  }

  function updateRule(idx: number, patch: Partial<VisibilityRule>) {
    setRules(
      rules.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, ...patch };
        if (patch.operator != null && patch.value === undefined) {
          next.value = coerceRuleValueForOperator(patch.operator, r.value);
        }
        return normalizeVisibilityRule(next);
      }),
    );
  }

  const conflictingEqualityLabels = (() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const rule of rules) {
      if ((rule.operator || "eq") !== "eq") continue;
      const key = String(rule.source_field_key || "").trim();
      if (!key) continue;
      const scope = normalizeSourceScope(rule.source_scope);
      const id = `${scope}:${key}`;
      const source = availableFields.find((f) => f.scope === scope && f.key === key);
      const current = counts.get(id) ?? { label: source?.label || key, count: 0 };
      current.count += 1;
      counts.set(id, current);
    }
    return [...counts.values()].filter((entry) => entry.count > 1).map((entry) => entry.label);
  })();

  return (
    <Section
      title="Sichtbarkeitsregeln"
      description="Sichtbar, wenn jede Regel zutrifft. Mehrere erlaubte Antworten desselben Feldes gehören in eine Regel mit Vergleich „ist einer von“. Beispiel: Material ist einer von Stahl-Glas, Holz-Glas oder Alu-Glas, und die Wartungsfrage ist gleich true."
      aside={
        rules.length === 0 ? (
          <StatusChip tone="neutral">Immer sichtbar</StatusChip>
        ) : (
          <StatusChip tone="ok">{rules.length} Regel{rules.length === 1 ? "" : "n"}</StatusChip>
        )
      }
    >
      {availableFields.length === 0 ? (
        <p className="mb-2 text-xs text-label-secondary">Keine Quellfelder verfügbar. Bitte Stammdaten- oder Wartungsfelder anlegen.</p>
      ) : null}
      {conflictingEqualityLabels.length > 0 ? (
        <Alert variant="warning" title="Diese Antworten schließen sich aus" className="mb-2">
          <p>
            {conflictingEqualityLabels.join(", ")} ist mehrfach mit „ist gleich“ belegt. Jede Regel muss
            zutreffen, deshalb bleibt das Feld unsichtbar. Die passenden Regeln werden zu „ist einer von“
            zusammengefasst. Die Regel zur Wartungsfrage bleibt daneben und muss zusätzlich erfüllt sein.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="mt-2"
            onClick={() => setRules(mergeDuplicateEqualityRules(rules))}
          >
            Zu „ist einer von“ zusammenfassen
          </Button>
        </Alert>
      ) : null}
      {rules.length > 0 ? (
        <div className="space-y-2">
          {rules.map((rule, idx) => {
            const sourceScope = normalizeSourceScope(rule.source_scope);
            const scopeFields = availableFields.filter((f) => f.scope === sourceScope);
            const sourceField =
              scopeFields.find((f) => f.key === rule.source_field_key) ?? null;
            return (
              <div key={idx} className="mb-rule-card">
                <div className="grid items-end gap-2 sm:grid-cols-5">
                  <SelectInput
                    label="Bereich"
                    value={sourceScope}
                    onChange={(e) =>
                      updateRule(idx, { source_scope: e.target.value, source_field_key: "", value: "" })
                    }
                  >
                    {BEHAVIOR_SOURCE_SCOPES.map((scope) => (
                      <option key={scope} value={scope}>
                        {BEHAVIOR_SOURCE_SCOPE_LABELS[scope]}
                      </option>
                    ))}
                  </SelectInput>
                  <SelectInput
                    label="Wenn Feld"
                    value={rule.source_field_key || ""}
                    onChange={(e) => {
                      const nextField = scopeFields.find((f) => f.key === e.target.value) ?? null;
                      updateRule(idx, {
                        source_field_key: e.target.value,
                        value: isBooleanSourceField(nextField) ? true : "",
                      });
                    }}
                  >
                    <option value="">Bitte wählen …</option>
                    {scopeFields.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label} ({s.key})
                      </option>
                    ))}
                  </SelectInput>
                  <SelectInput
                    label="Vergleich"
                    value={rule.operator || "eq"}
                    onChange={(e) => updateRule(idx, { operator: e.target.value })}
                  >
                    {operatorChoicesForSourceField(sourceField, rule.operator).map((op) => (
                      <option key={op} value={op}>
                        {BEHAVIOR_OPERATOR_LABELS[op] || op}
                      </option>
                    ))}
                  </SelectInput>
                  <RuleValueInput
                    value={rule.value}
                    sourceField={sourceField}
                    operator={rule.operator || "eq"}
                    onChange={(value) => updateRule(idx, { value })}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRules(rules.filter((_, i) => i !== idx))}
                  >
                    Entfernen
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="mt-2"
        onClick={() =>
          setRules([
            ...rules,
            {
              rule_type: "visibleIf",
              source_scope: "inspection",
              source_field_key: "",
              operator: "eq",
              value: "",
            },
          ])
        }
      >
        Regel hinzufügen
      </Button>
    </Section>
  );
}

function MaterialLinkEditor({
  draft,
  allFields,
  onPatch,
}: {
  draft: BuilderField;
  allFields: BuilderField[];
  onPatch: (mutator: (f: BuilderField) => void) => void;
}) {
  const link = (draft.options_override_json.material_link as {
    defect_field_key?: string;
    masterdata_field_key?: string;
    qty?: number;
    unit?: string;
    overwrite_mode?: string;
  } | undefined) || {};

  const defectFields = allFields.filter((f) => {
    if (f.scope !== "inspection" || !f.field_key) return false;
    if (f.semantic_role === "defect") return true;
    return f.type === "checkbox" || f.type === "bool" || f.type === "select" || f.type === "multiselect" || f.type === "choice";
  });
  const masterdataArticleFields = allFields.filter(
    (f) =>
      f.scope === "masterdata" &&
      (f.type === "article_ref" || f.type === "article_ref_list") &&
      f.field_key,
  );

  function setLink(partial: Record<string, unknown>) {
    onPatch((f) => {
      const current = (f.options_override_json.material_link as Record<string, unknown> | undefined) || {};
      const next = { ...current, ...partial };
      const defectFieldKey = String(next.defect_field_key || "").trim();
      const masterdataFieldKey = String(next.masterdata_field_key || "").trim();
      if (!defectFieldKey && !masterdataFieldKey) {
        const { material_link: _removed, ...rest } = f.options_override_json;
        f.options_override_json = rest;
        return;
      }
      f.options_override_json = {
        ...f.options_override_json,
        material_link: {
          defect_field_key: defectFieldKey,
          masterdata_field_key: masterdataFieldKey,
          qty: Number(next.qty) > 0 ? Number(next.qty) : 1,
          unit: String(next.unit || "Stk").trim() || "Stk",
          overwrite_mode: next.overwrite_mode === "manual" ? "manual" : "auto",
        },
      };
    });
  }

  const emptyHints: string[] = [];
  if (!defectFields.length) {
    emptyHints.push("Kein Wartungsfeld als Mangel-Rolle oder Ja/Nein/Auswahl vorhanden.");
  }
  if (!masterdataArticleFields.length) {
    emptyHints.push("Kein Stammdatenfeld vom Typ Artikelreferenz vorhanden.");
  }

  return (
    <Section
      title="Verknüpfung konfigurieren"
      description="Bei getriggertem Mangel wird der Artikel aus dem Stammdatenfeld automatisch übernommen. Im Formular kann zusätzlich manuell ergänzt oder überschrieben werden."
    >
      {emptyHints.length ? (
        <Alert variant="warning" className="mb-3">
          {emptyHints.join(" ")}
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <SelectInput
          label="Mängelfeld"
          value={String(link.defect_field_key || "")}
          onChange={(e) => setLink({ defect_field_key: e.target.value })}
        >
          <option value="">— wählen —</option>
          {defectFields.map((f) => (
            <option key={f.field_key} value={f.field_key}>
              {f.label || f.field_key}
              {f.semantic_role !== "defect" ? " (Rolle noch nicht „Mangel“)" : ""}
            </option>
          ))}
        </SelectInput>
        <SelectInput
          label="Stammdaten-Artikel"
          value={String(link.masterdata_field_key || "")}
          onChange={(e) => setLink({ masterdata_field_key: e.target.value })}
        >
          <option value="">— wählen —</option>
          {masterdataArticleFields.map((f) => (
            <option key={f.field_key} value={f.field_key}>
              {f.label || f.field_key}
            </option>
          ))}
        </SelectInput>
        <TextInput
          label="Menge"
          type="number"
          min={0.01}
          step={0.01}
          value={String(link.qty ?? 1)}
          onChange={(e) => setLink({ qty: Number(e.target.value) })}
        />
        <TextInput
          label="Einheit"
          value={String(link.unit || "Stk")}
          onChange={(e) => setLink({ unit: e.target.value })}
        />
        <SelectInput
          label="Überschreibmodus"
          value={link.overwrite_mode === "manual" ? "manual" : "auto"}
          onChange={(e) => setLink({ overwrite_mode: e.target.value })}
        >
          <option value="auto">Automatisch (Regel gewinnt)</option>
          <option value="manual">Manuell überschreibbar</option>
        </SelectInput>
      </div>
    </Section>
  );
}

function GradeSelect({
  label,
  value,
  enabledGrades,
  onChange,
}: {
  label: string;
  value: number | null;
  enabledGrades: number[];
  onChange: (g: number | null) => void;
}) {
  return (
    <SelectInput
      label={label}
      hint="Optional – diese Note wird bei ausgelöstem Mangel in die Bewertung übernommen."
      value={value == null ? "" : String(value)}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
    >
      <option value="">keine</option>
      {enabledGrades.map((g) => (
        <option key={g} value={g} style={{ color: MAINTENANCE_GRADE_COLORS[g] }}>
          Note {g}
        </option>
      ))}
    </SelectInput>
  );
}
